import React from 'react';
import { Box, Container, Grid, Stack, Typography, Paper } from '@mui/material';
import HubRoundedIcon from '@mui/icons-material/HubRounded';
import HandshakeRoundedIcon from '@mui/icons-material/HandshakeRounded';
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';

import Hero from '../components/Hero';
import SectionHeading from '../components/SectionHeading';
import FeatureCard from '../components/FeatureCard';
import CTASection from '../components/CTASection';
import { brand, siteLogoUrl } from '../theme';

const VALUES = [
  {
    icon: HubRoundedIcon,
    title: 'Connect everything',
    description:
      '“Nexus” means a connection. We build the layer between your phones, calendars, payments, and people.',
  },
  {
    icon: HandshakeRoundedIcon,
    title: 'Operator-first',
    description:
      'We design with the people who actually run the business — not just the executives buying the software.',
  },
  {
    icon: RocketLaunchRoundedIcon,
    title: 'Ship fast, iterate honestly',
    description:
      'Working software in weeks, not quarters. Real feedback loops. No theater dashboards.',
  },
  {
    icon: VerifiedRoundedIcon,
    title: 'AI you can trust',
    description:
      'Confidence scoring, escalation paths, and auditable conversations. The AI knows when to ask for help.',
    accent: 'gold',
  },
];

export default function About() {
  return (
    <Box>
      <Hero
        title="The connective layer between your business and AI."
        subtitle="G&R Nexus builds custom software and intelligent solutions for the operators of real businesses. Concierge is what we built first — because answering the phone matters."
        primaryCta={{ label: 'Meet Concierge', to: '/concierge' }}
        secondaryCta={{ label: 'Get in touch', to: '/contact' }}
        showLogo={false}
      />

      <Box sx={{ py: { xs: 10, md: 14 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  borderRadius: 4,
                  overflow: 'hidden',
                  border: `1px solid ${brand.border}`,
                  boxShadow: '0 30px 60px -30px rgba(15,23,42,0.25)',
                  bgcolor: '#fff',
                }}
              >
                <Box
                  component="img"
                  src={siteLogoUrl}
                  alt="G&R Nexus"
                  sx={{ width: '100%', height: 'auto', display: 'block', p: { xs: 2, md: 3 } }}
                />
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography
                variant="overline"
                sx={{ color: brand.blue, fontWeight: 700, letterSpacing: '0.18em' }}
              >
                Our story
              </Typography>
              <Typography variant="h2" sx={{ mt: 1.5, mb: 3, fontSize: { xs: '2rem', md: '2.6rem' } }}>
                Software that respects how the work actually gets done.
              </Typography>
              <Stack spacing={2.5}>
                <Typography variant="body1" sx={{ color: brand.slate }}>
                  G&amp;R Nexus started inside the operations of real service businesses — the kind
                  where a missed call is a missed job, and a 10-minute admin task done 50 times a
                  day is the difference between a good month and a bad one.
                </Typography>
                <Typography variant="body1" sx={{ color: brand.slate }}>
                  We built Concierge to solve those problems first, in production, against real
                  customers. Now we help other businesses adopt the same playbook — sometimes as a
                  product, sometimes as a custom build, always tuned to how their team actually
                  works.
                </Typography>
                <Typography variant="body1" sx={{ color: brand.slate }}>
                  <strong style={{ color: brand.navy }}>Custom Software. Intelligent Solutions.</strong>{' '}
                  isn’t a tagline — it’s the order we work in.
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 10, md: 14 }, bgcolor: '#FFFFFF', borderTop: `1px solid ${brand.border}`, borderBottom: `1px solid ${brand.border}` }}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow="What we believe"
            title="Four principles behind every Nexus build."
          />
          <Grid container spacing={3}>
            {VALUES.map((v) => (
              <Grid key={v.title} size={{ xs: 12, sm: 6, md: 3 }}>
                <FeatureCard {...v} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 10, md: 14 } }}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow="Trust strip"
            title="Built on the tools your operators already trust."
            subtitle="Concierge runs on AWS infrastructure with serverless APIs, integrates with Twilio, OpenPhone, Amazon Connect, Google Calendar, and Stripe — and uses Amazon Bedrock for production AI."
          />
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 5 },
              borderRadius: 4,
              border: `1px solid ${brand.border}`,
            }}
          >
            <Stack
              direction="row"
              flexWrap="wrap"
              justifyContent="center"
              spacing={4}
              useFlexGap
              sx={{ rowGap: 2 }}
            >
              {['AWS', 'Amazon Bedrock', 'Twilio', 'OpenPhone', 'Amazon Connect', 'Google Calendar', 'Stripe'].map((b) => (
                <Typography
                  key={b}
                  variant="h5"
                  sx={{
                    color: brand.slate,
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    opacity: 0.85,
                  }}
                >
                  {b}
                </Typography>
              ))}
            </Stack>
          </Paper>
        </Container>
      </Box>

      <CTASection
        eyebrow="Work with Nexus"
        title="Custom build, Concierge, or both — let’s figure out the right fit."
        subtitle="Tell us about your business and the workflows you wish would just run themselves."
      />
    </Box>
  );
}
